import { Vector2 } from "../../../common/vector2.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement, subtreeHasUnloadedImages } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextGradient } from "../../common_ui_elements/ui_text_element.js";
import { Game } from "../../../game.js";
import { SafeAreas } from "../../safe_areas.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { ChainItemBase, ChainItemConfig, ChainItemType, ChainReward, ChainRewardType } from "./chain_item_base.js";
import { renderElementAt } from "../../common_ui_elements/render_utils.js";
import { BackdropLayer } from "../../common_ui_elements/backdrop_layer.js";

/** Reward payload as the source hands it over: amounts, or `true` for permanent No Ads. */
export type ChainRewards = Record<string, number | boolean | undefined>;

export interface ChainStepView
{
  id: string;
  free?: boolean;
  /** A non-store step that must be unlocked by a completed rewarded video. */
  rewarded?: boolean;
  rewards: ChainRewards;
  /** Store price for a paid step. A paid step without one is skipped, as it has nothing to charge. */
  price?: string;
}

/** The only shape the board needs, so it stays ignorant of DTOs and of any backend. */
export interface ChainView
{
  offer_id: string;
  steps: ChainStepView[];
  /** Card the player may interact with right now. */
  active_step_id: string | null;
  /** After the last step the chain restarts from the first one. */
  repeat: boolean;
}

/** One stroked pass of the title; passes are drawn widest-first. */
export interface ChainTitleLayer
{
  outline_width_design: number;
  outline_offset_y_design: number;
  outline_color?: string;
  outline_gradient_color?: UITextGradient;
  shadow_blur_design?: number;
  shadow_color?: string;
  shadow_offset_y_design?: number;
}

export interface ChainTitleStyle
{
  font_size_design: number;
  text_color: string;
  gradient_text_color?: UITextGradient;
  /** Literal caption, or a localization key — whichever the screen uses. */
  text?: string;
  localization_key?: string;
  layers: ChainTitleLayer[];
}

export type ChainExitReason = 'user_close' | 'chain_complete' | 'offer_gone';

/**
 * The board holder. It notes whether anything besides the cards changed while
 * the dirty flag is still per child: once it spreads, the whole subtree reads
 * as dirty and a card animation can no longer be told from a title change.
 */
class ChainContentHolder extends UIElementHolder
{
  public static_dirty = false;

  protected override onChildrenUpdated( any_child_dirty: boolean ): void
  {
    super.onChildrenUpdated( any_child_dirty );
    if ( !any_child_dirty || this.static_dirty ) return;
    this.static_dirty = this.getChildren().some( child => !( child instanceof ChainItemBase ) && child.shouldBeRendered() );
  }
}

/**
 * The snake-shaped board of chain cards: slot layout, the walk order along the
 * path, and every animation that moves cards along it — appear, shift, unlock,
 * reveal, exit. Subclasses own the screen's chrome (background, decor, title,
 * timer) and say where the chain data comes from.
 */
export abstract class ChainScreenBase extends UIElement
{
  private static readonly SNAKE_SHIFT_MOVES: readonly ( readonly [ number, number ] )[] = [
    [ 1, 0 ], [ 3, 1 ], [ 2, 3 ], [ 4, 2 ], [ 5, 4 ],
  ];

  private static readonly SNAKE_SHIFT_MS = 450;

  /**
   * How long before the shift ends the incoming card starts popping in, so the
   * last slot is never left visibly empty waiting for the chain to settle.
   */
  private static readonly REVEAL_LEAD_MS = 220;

  /**
   * Slot each chain step occupies, in the order the snake walks the path:
   * the chain climbs down the S-shape, so a step's queue position is not its
   * slot index.
   */
  private static readonly SNAKE_SLOT_ORDER: readonly number[] = [ 0, 1, 3, 2, 4, 5 ];

  private static readonly OFFER_APPEAR_STAGGER_MS = 72;

  private events_controller: BasicEventsController | null = null;
  private background_gradient: CanvasGradient | null = null;
  private timer_bg: UIImage | null = null;
  private timer_text: UITextElement | null = null;
  private timer_refreshed_at = 0;
  private last_timer_text = "";
  private timer_slot_index = 0;

  protected content: UIElementHolder | null = null;

  // Everything but the cards (background, art, title, timer, chain) is painted
  // on a backdrop canvas under the screen canvas, only when it changes: the
  // cards' appear/shift animations used to repaint the multi-pass outlined
  // title and all art every frame (27 ms frames on open).
  private backdrop: BackdropLayer | null = null;
  private static_dirty = true;
  /** Set while statics were painted with images still loading; repaint once they land. */
  private statics_retry_at = 0;
  // The first render can come before the first update(), which then moves
  // every element to its render position and repaints it all a frame later.
  private updated_once = false;
  protected content_x = 0;

  private offer_items: ( ChainItemBase | null )[] = [];
  private offer_positions: { x: number; y: number }[] = [];
  private offer_w = 0;
  private offer_h = 0;
  /** Index into the chain steps of the next card to slide onto the board. */
  private next_reveal_index = 0;
  private shown_tracked = false;
  protected exiting = false;

  private readonly handleChainChanged = (): void => { this.reconcileWithSource(); };

  // Public so a subclass with nothing to add can simply inherit it; the class
  // being abstract already prevents direct construction.
  public constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );
    window.addEventListener( this.getChangeEventName(), this.handleChainChanged );

    this.createBackdrop();
    this.createConfetti();
    this.createDecor();
    this.createContent();
  }

  // ---- subclass contract ----

  /** Current chain, normalized from whatever backs this screen. */
  protected abstract getChainView(): ChainView | null;

  /** Builds the concrete card type this screen shows. */
  protected abstract createItem( x: number, y: number, width: number, height: number, config: ChainItemConfig ): ChainItemBase;

  /** Window event announcing that the chain moved on its own. */
  protected abstract getChangeEventName(): string;

  /** Vertical background stops, top to bottom. */
  protected abstract getBackgroundStops(): { offset: number; color: string }[];

  /** Font, fill and stroked passes of the headline. */
  protected abstract getTitleStyle(): ChainTitleStyle;

  protected abstract getTimerImgId(): ImgID;

  /** Milliseconds left on the offer, or null when it has no deadline. */
  protected abstract getRemainingMs(): number | null;

  protected abstract formatRemaining( ms: number | null ): string;

  /** Art behind the confetti. Most screens have none. */
  protected createBackdrop(): void {}

  /** The character or prop above the content block. */
  protected createDecor(): void {}

  protected getConfettiImgId(): ImgID { return ImgID.UI_ChainOfferConfetti; }

  protected getSnakeChainImgId(): ImgID { return ImgID.UI_ChainOfferSnakeChain; }

  /** Fired once, the first time a populated board is shown. */
  protected onShown( offer_id: string ): void {}

  /** Fired once, when the screen leaves for the menu. */
  protected onClosed( offer_id: string, reason: ChainExitReason ): void {}

  /** Hook for per-card follow-up work, e.g. resolving a store price asynchronously. */
  protected onItemCreated( item: ChainItemBase, step: ChainStepView ): void {}

  // ---- board ----

  protected createContent(): void
  {
    const content_w_design = 1040;
    const content_h_design = 1683;
    const content_y_min_design = 220;
    const content_y_max_design = 623;

    const content_w = LayoutScaler.widthFromDesign( content_w_design );
    const content_h = LayoutScaler.heightFromDesign( content_h_design );
    const content_x = ( this.rect.getW() - content_w ) / 2;
    const content_y = LayoutScaler.yFromLayouts( content_y_min_design, content_y_max_design ) - LayoutScaler.yFromDesign( 100 );

    const content = new ChainContentHolder( this.ctx, content_x, content_y, content_w, content_h );
    this.addChild( content );
    this.content = content;
    this.content_x = content_x;

    this.createContentTitle( content );
    this.createCloseButton( content_x, content_y );
    this.createContentTimer( content );
    this.createContentSnakeChain( content );
    this.createOffers( content );
  }

  private createOffers( content: UIElementHolder ): void
  {
    const offer_w_design = 496;
    const offer_h_design = 412;
    const top_y_design = 350;
    const side_y_design = 810;

    const offer_w = LayoutScaler.widthFromDesign( offer_w_design );
    const offer_h = LayoutScaler.heightFromDesign( offer_h_design );
    const left_x = 0;
    const right_x = content.getRect().getW() - offer_w;
    const top_y = LayoutScaler.yFromDesign( top_y_design );
    const side_y = LayoutScaler.yFromDesign( side_y_design );
    const bottom_y = content.getRect().getH() - offer_h;

    const offer_positions = [
      { x: left_x, y: top_y },
      { x: right_x, y: top_y },
      { x: left_x, y: side_y },
      { x: right_x, y: side_y },
      { x: left_x, y: bottom_y },
      { x: right_x, y: bottom_y },
    ];
    this.offer_w = offer_w;
    this.offer_h = offer_h;
    this.offer_positions = offer_positions;
    this.populateOffers();
  }

  /**
   * A looping chain repeats its configured cards until it fills every visual
   * slot on the path, so a 4-step loop still occupies all 6 slots.
   */
  private getChainStepAt( index: number ): { chain: ChainView; step: ChainStepView } | null
  {
    const chain = this.getChainView();
    if ( !chain || !chain.steps.length ) return null;

    if ( chain.repeat ) return { chain, step: chain.steps[index % chain.steps.length] };
    return index < chain.steps.length ? { chain, step: chain.steps[index] } : null;
  }

  private createOfferItem( chain: ChainView, step: ChainStepView, slot: number, locked: boolean ): ChainItemBase | null
  {
    const content = this.content;
    if ( !content ) return null;

    const is_rewarded = step.rewarded === true;
    const is_paid = !step.free && !is_rewarded;
    // A paid step with no resolvable product has nothing to charge, so it never reaches the board.
    if ( is_paid && step.price === undefined ) return null;

    const rewards: ChainReward[] = Object.entries( step.rewards )
      .filter( ( [ type, amount ] ) => ( type === 'no_ads' && amount === true ) || ( typeof amount === 'number' && amount > 0 ) )
      .map( ( [ type, amount ] ) => ( {
        type: type as ChainRewardType,
        ...( typeof amount === 'number' ? { amount } : {} ),
      } ) )
      // Permanent No Ads is the most important part of a bundle, so it must
      // never be hidden behind the card's compact reward layout.
      .sort( ( left, right ) => Number( right.type === 'no_ads' ) - Number( left.type === 'no_ads' ) );
    const position = this.offer_positions[slot];

    const item = this.createItem( position.x, position.y, this.offer_w, this.offer_h, {
      offer_id: chain.offer_id,
      step_id: step.id,
      type: step.free ? ChainItemType.Free : is_rewarded ? ChainItemType.Video : ChainItemType.Price,
      locked,
      ...( is_paid ? { price: step.price } : {} ),
      rewards,
    } );
    content.addChild( item );
    this.offer_items[slot] = item;

    // Only the current card may be interacted with. Free cards become
    // claimable after the preceding paid purchase.
    if ( !locked )
      this.armOfferExit( item );

    this.onItemCreated( item, step );

    return item;
  }

  protected populateOffers(): void
  {
    const content = this.content;
    if ( !content ) return;
    for ( const item of this.offer_items ) if ( item ) content.removeChild( item );
    this.offer_items = new Array( this.offer_positions.length ).fill( null );

    const chain = this.getChainView();
    if ( !chain || !chain.active_step_id ) return;

    const active_index = chain.steps.findIndex( step => step.id === chain.active_step_id );
    if ( active_index < 0 ) return;

    if ( !this.shown_tracked )
    {
      this.shown_tracked = true;
      this.onShown( chain.offer_id );
    }

    ChainScreenBase.SNAKE_SLOT_ORDER.forEach( ( slot, order ) =>
    {
      const next = this.getChainStepAt( active_index + order );
      if ( next ) this.createOfferItem( next.chain, next.step, slot, order !== 0 );
    } );

    this.next_reveal_index = active_index + this.offer_positions.length;

    this.playOffersAppearAnimation();
  }

  /**
   * Re-syncs the board after the chain moves on its own. The snake shift already
   * walks the board forward by one, so only a bigger jump needs a rebuild.
   */
  private reconcileWithSource(): void
  {
    if ( !this.content || !this.offer_positions.length || this.exiting )
      return;

    const chain = this.getChainView();

    if ( !chain || !chain.active_step_id )
    {
      if ( this.shown_tracked )
        this.exitToMenu( 'offer_gone' );
      return;
    }

    const source_index = chain.steps.findIndex( step => step.id === chain.active_step_id );
    if ( source_index < 0 )
      return;

    const local_id = this.offer_items[0]?.getStepId();
    const local_index = local_id ? chain.steps.findIndex( step => step.id === local_id ) : -1;

    if ( local_index >= 0 )
    {
      const advanced_by = ( source_index - local_index + chain.steps.length ) % chain.steps.length;
      if ( advanced_by <= 1 )
        return;
    }

    this.populateOffers();
  }

  protected exitToMenu( reason: ChainExitReason ): void
  {
    if ( this.exiting )
      return;
    this.exiting = true;

    const offer_id = this.getChainView()?.offer_id;
    if ( offer_id )
      this.onClosed( offer_id, reason );

    void ScreenManager.showScreen( Screens.MENU );
  }

  private playOffersAppearAnimation(): void
  {
    const start_base = performance.now();
    const reveal_order = [ ...ChainScreenBase.SNAKE_SLOT_ORDER ].reverse(); // queue order 5 → 0

    reveal_order.forEach( ( slot, i ) =>
    {
      this.offer_items[ slot ]?.playAppearAnimation( start_base + i * ChainScreenBase.OFFER_APPEAR_STAGGER_MS );
    } );
  }

  private playSnakeShift(): void
  {
    if ( !this.content )
      return;

    // The purchased card is still playing its own exit here; it detaches itself
    // when that ends, so it must not be pulled out of the tree by the shift.
    const now = performance.now();

    for ( const [ from_index, to_index ] of ChainScreenBase.SNAKE_SHIFT_MOVES )
    {
      const item = this.offer_items[ from_index ];
      if ( !item )
      {
        this.offer_items[ to_index ] = null;
        continue;
      }

      this.content.removeChild( item );
      this.content.addChild( item );

      const dest_pos = this.offer_positions[ to_index ];
      const move = new TransformStraightMove( item, true, null, new Vector2( dest_pos.x, dest_pos.y ), now, ChainScreenBase.SNAKE_SHIFT_MS, { easing: Easing.EaseInOutQuad } );

      if ( to_index === 0 )
        move.setOnFinished( () => this.activateOffer( item ) );

      item.addTransform( move );

      this.offer_items[ to_index ] = item;
    }

    this.offer_items[ 5 ] = null;
    this.revealNextOffer( now + Math.max( 0, ChainScreenBase.SNAKE_SHIFT_MS - ChainScreenBase.REVEAL_LEAD_MS ) );
  }

  /**
   * The purchased card keeps animating after the chain starts shifting, so it
   * detaches itself once its own exit ends instead of being pulled out by the shift.
   */
  private armOfferExit( item: ChainItemBase ): void
  {
    item.setOnExitStarted( () => this.playSnakeShift() );
    item.setOnExitFinished( () =>
    {
      this.content?.removeChild( item );

      if ( this.offer_items.every( it => !it ) )
        this.exitToMenu( 'chain_complete' );
    } );
  }

  private activateOffer( item: ChainItemBase ): void
  {
    item.unlock();
    this.armOfferExit( item );
  }

  private revealNextOffer( start_time: number ): void
  {
    if ( !this.content )
      return;

    const next = this.getChainStepAt( this.next_reveal_index );
    if ( !next )
      return;

    this.next_reveal_index++;

    const last_slot = ChainScreenBase.SNAKE_SLOT_ORDER[ ChainScreenBase.SNAKE_SLOT_ORDER.length - 1 ];
    const item = this.createOfferItem( next.chain, next.step, last_slot, true );
    item?.playAppearAnimation( start_time );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  protected createConfetti(): void
  {
    const confetti_w_design = 1275;
    const confetti_h_design = 1744;
    const confetti_y_min_design = 163;
    const confetti_y_max_design = 109;

    const base_confetti_w = LayoutScaler.widthFromDesign( confetti_w_design );
    const base_confetti_h = LayoutScaler.heightFromDesign( confetti_h_design );
    const base_confetti_top = -LayoutScaler.yFromLayouts( confetti_y_min_design, confetti_y_max_design );

    // On wide screens the design-width art leaves bare strips at the sides —
    // stretch it to cover the full width, keeping aspect ratio and pinning the
    // bottom edge so the extra height only spills further off the top.
    const confetti_w = Math.max( base_confetti_w, this.rect.getW() );
    const confetti_scale = confetti_w / base_confetti_w;
    const confetti_h = base_confetti_h * confetti_scale;
    const confetti_x = ( this.rect.getW() - confetti_w ) / 2;
    const confetti_y = base_confetti_top + base_confetti_h - confetti_h;

    const confetti = new UIImage( this.ctx, confetti_x, confetti_y, confetti_w, confetti_h, this.getConfettiImgId() );
    this.addChild( confetti );
  }

  protected createContentSnakeChain( content: UIElementHolder ): void
  {
    const snake_chain_w_design = 692;
    const snake_chain_h_design = 1076;
    const snake_chain_x_design = 175;
    const snake_chain_y_design = 466;

    const snake_chain_w = LayoutScaler.widthFromDesign( snake_chain_w_design );
    const snake_chain_h = LayoutScaler.heightFromDesign( snake_chain_h_design );
    const snake_chain_x = LayoutScaler.xFromDesign( snake_chain_x_design );
    const snake_chain_y = LayoutScaler.yFromDesign( snake_chain_y_design );

    const snake_chain = new UIImage( this.ctx, snake_chain_x, snake_chain_y, snake_chain_w, snake_chain_h, this.getSnakeChainImgId() );
    content.addChild( snake_chain );
  }

  protected createCloseButton( content_x: number, content_y: number ): void
  {
    const close_icon_w_design = 110;
    const close_icon_h_design = 110;
    const close_icon_x_design = 897;
    const close_icon_y_design = 0;

    const close_icon_w = LayoutScaler.widthFromDesign( close_icon_w_design );
    const close_icon_h = LayoutScaler.heightFromDesign( close_icon_h_design );
    const close_icon_x = content_x + LayoutScaler.xFromLayouts( close_icon_x_design , close_icon_x_design + 200  );
    const close_icon_y = content_y + LayoutScaler.yFromDesign( close_icon_y_design );

    const close_icon = new UIImage( this.ctx, close_icon_x, close_icon_y, close_icon_w, close_icon_h, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    this.addChild( close_icon );

    close_icon.setOnClick( () => this.exitToMenu( 'user_close' ) );
  }

  protected createContentTimer( content: UIElementHolder ): void
  {
    this.timer_slot_index = content.getChildren().length;
    this.buildTimer();
  }

  protected buildTimer(): void
  {
    const content = this.content;
    if ( !content || this.timer_bg ) return;

    const remaining_ms = this.getRemainingMs();
    if ( remaining_ms === null ) return;

    const timer_w_design = 288;
    const timer_h_design = 78;
    const timer_y_design = 273;
    const timer_font_size_design = 45;
    const icon_area_w_design = 72;
    const text_gap_design = 14;

    const timer_w = LayoutScaler.widthFromDesign( timer_w_design );
    const timer_h = LayoutScaler.heightFromDesign( timer_h_design );
    const timer_x = ( content.getRect().getW() - timer_w ) / 2;
    const timer_y = LayoutScaler.yFromDesign( timer_y_design );

    const timer_bg = new UIImage( this.ctx, timer_x, timer_y, timer_w, timer_h, this.getTimerImgId() );
    content.addChildAtIndex( timer_bg, Math.min( this.timer_slot_index, content.getChildren().length ) );

    const text_gap = LayoutScaler.xFromDesign( text_gap_design );
    const text_x = LayoutScaler.widthFromDesign( icon_area_w_design );
    const text_w = timer_w - text_x - text_gap;

    this.last_timer_text = this.formatRemaining( remaining_ms );
    const timer_text = new UITextElement( this.ctx, text_x, 0, text_w, timer_h, {
      text: this.last_timer_text,
      font_size: LayoutScaler.textSizeFromDesign( timer_font_size_design ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      center_on_cap_height: true,
      ...TextStyleScheme.timerShadow(),
      text_color: ColorScheme.white,
      scale_to_width: true,
      auto_wrap: false,
    } );
    timer_bg.addChild( timer_text, true );

    this.timer_bg = timer_bg;
    this.timer_text = timer_text;
  }

  protected refreshTimer(): void
  {
    const remaining_ms = this.getRemainingMs();

    if ( remaining_ms === null )
    {
      this.reconcileWithSource();
      if ( this.content && this.timer_bg )
      {
        this.content.removeChild( this.timer_bg );
        this.content.forceMakeShouldBeRendered();
        this.timer_bg = null;
        this.timer_text = null;
      }
      return;
    }

    this.buildTimer();

    const text = this.formatRemaining( remaining_ms );
    if ( text === this.last_timer_text ) return;

    this.last_timer_text = text;
    this.timer_text?.setText( text );
  }

  protected createContentTitle( content: UIElementHolder ): void
  {
    const title_y_design = 113;
    const title_h_design = 136;

    const style = this.getTitleStyle();

    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_h = LayoutScaler.heightFromDesign( title_h_design );

    const title_w_ratio = 0.8;
    const screen_w = this.rect.getW();
    const title_w = screen_w * title_w_ratio;
    const title_x = ( screen_w - title_w ) / 2 - this.content_x;

    for ( const layer of style.layers )
    {
      const title = new UITextElement( this.ctx, title_x, title_y, title_w, title_h, {
        text: style.text,
        localization_key: style.localization_key,
        font_size: LayoutScaler.textSizeFromDesign( style.font_size_design ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        text_color: style.text_color,
        gradient_text_color: style.gradient_text_color,
        outline_width: LayoutScaler.textBorderSizeFromDesign( layer.outline_width_design ),
        outline_color: layer.outline_color,
        outline_gradient_color: layer.outline_gradient_color,
        outline_offset_y: LayoutScaler.yFromDesign( layer.outline_offset_y_design ),
        shadow_blur: layer.shadow_blur_design !== undefined ? LayoutScaler.textBorderSizeFromDesign( layer.shadow_blur_design ) : undefined,
        shadow_color: layer.shadow_color,
        shadow_offset_y: layer.shadow_offset_y_design !== undefined ? LayoutScaler.yFromDesign( layer.shadow_offset_y_design ) : undefined,
      } );
      content.addChild( title );
    }
  }

  private getBackgroundGradient(): CanvasGradient
  {
    if ( !this.background_gradient )
    {
      const gradient = this.ctx.createLinearGradient( 0, 0, 0, Game.screenH() );
      for ( const stop of this.getBackgroundStops() )
        gradient.addColorStop( stop.offset, stop.color );
      this.background_gradient = gradient;
    }

    return this.background_gradient;
  }

  protected override onChildrenUpdated( any_child_dirty: boolean ): void
  {
    super.onChildrenUpdated( any_child_dirty );
    if ( any_child_dirty && !this.static_dirty )
      this.static_dirty = this.getChildren().some( child => child !== this.content && child.shouldBeRendered() );
  }

  private isCard( element: UIElement ): boolean
  {
    return element instanceof ChainItemBase;
  }

  public render(): void
  {
    const now = performance.now();
    if ( !this.updated_once ) this.update( now );

    const content = this.content;
    const holder = content instanceof ChainContentHolder ? content : null;
    const retry_due = this.statics_retry_at > 0 && now >= this.statics_retry_at;

    if ( !this.shouldBeRendered() && !retry_due )
      return;

    const content_children = content ? content.getChildren() : [];
    const root_children = this.getChildren();
    const content_index = content ? root_children.indexOf( content ) : root_children.length;
    const statics: UIElement[] = [
      ...root_children.slice( 0, content_index ),
      ...content_children.filter( child => !this.isCard( child ) ),
    ];

    if ( !this.backdrop && !this.exiting )
    {
      this.backdrop = BackdropLayer.under( this.ctx.canvas );
      this.static_dirty = true;
    }

    const paint_statics = ( ctx: CanvasRenderingContext2D ): void =>
    {
      ctx.fillStyle = this.getBackgroundGradient();
      ctx.fillRect( 0, 0, Game.screenW(), Game.screenH() );
      for ( const element of statics )
        renderElementAt( ctx, element );
      this.statics_retry_at = statics.some( element => subtreeHasUnloadedImages( element ) ) ? now + 250 : 0;
    };

    if ( this.backdrop && ( this.static_dirty || holder?.static_dirty || retry_due ) )
      paint_statics( this.backdrop.ctx );
    this.static_dirty = false;
    if ( holder ) holder.static_dirty = false;

    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height );
    ctx.restore();
    // Without a backdrop (it could not be created) the statics go under the cards here.
    if ( !this.backdrop )
      paint_statics( ctx );
    for ( const child of content_children )
      if ( this.isCard( child ) )
        renderElementAt( ctx, child );
    for ( const child of root_children.slice( content_index + 1 ) )
      renderElementAt( ctx, child );
    SafeAreas.renderBothSafeAreaOverlays( ctx );

    // Nothing here is painted through the tree walk, so flags left over from
    // this frame's spread would read as fresh changes on the next update.
    this.skipRenderSubtree();
  }

  public update( now: number ): void
  {
    this.updated_once = true;
    if ( now - this.timer_refreshed_at > 1000 )
    {
      this.timer_refreshed_at = now;
      this.refreshTimer();
    }
    this.updateBasic( now );
  }

  public destroy(): void
  {
    this.exiting = true;
    this.backdrop?.destroy();
    this.backdrop = null;
    window.removeEventListener( this.getChangeEventName(), this.handleChainChanged );

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }
}
